import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import { AIChatCitationFragment, StaffAIChatSessionPreviewFragment } from 'types/graphql'
import { v4 as uuidv4 } from 'uuid'

import useDisclosure, { UseDisclosureReturn } from 'src/hooks/use-disclosure'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'

// Type for the chat storage IDs - unique identifiers for chat sessions
type ChatStorageIds = {
  chatLocalStorageId: string
  chatSessionStorageId: string
}

// Message type for Staff AI Chat
export type StaffAIChatMessage = {
  id: string | number
  text: string
  sender: 'user' | 'ai'
  questionCreatedAt?: string
  responseCreatedAt?: string
  isThinking?: boolean
  citations?: AIChatCitationFragment[]
}

// Context value type for the Staff AI Assistant
export type StaffAIAssistantContextValue = {
  // Overlay state
  overlay: UseDisclosureReturn
  // Message state - persists across overlay open/close within same React session
  messages: StaffAIChatMessage[]
  setMessages: React.Dispatch<React.SetStateAction<StaffAIChatMessage[]>>
  // Typing indicator state
  isAssistantTyping: boolean
  setIsAssistantTyping: React.Dispatch<React.SetStateAction<boolean>>

  // Session management for past conversations
  activeSessionId: number | null
  chatStorageIds: ChatStorageIds
  isLoadingSession: boolean
  setActiveSessionId: (sessionId: number | null) => void
  setIsLoadingSession: (loading: boolean) => void
  startNewConversation: () => void
}

// Context for controlling the Staff AI Assistant overlay and persisting conversation
// Using React Context because:
// - We plan to open this from multiple places (Navbar, keyboard shortcut, etc.)
// - Messages should persist across overlay open/close within the same browser session
// - Users can start a fresh chat when they want via "New Chat" button
const StaffAIAssistantContext = createContext<StaffAIAssistantContextValue | null>(null)

// Returns null if used outside the provider - callers should handle the null case
export const useStaffAIAssistant = (): StaffAIAssistantContextValue | null => {
  return useContext(StaffAIAssistantContext)
}

type Props = {
  children: React.ReactNode
}

/**
 * Generates new chat storage IDs for a fresh conversation.
 * Uses browserId for local storage ID and a new UUID for session storage ID.
 */
const useNewChatStorageIds = (browserId: string): (() => ChatStorageIds) => {
  return useCallback(() => {
    const uniqueId = uuidv4()
    return {
      chatLocalStorageId: `${browserId}-chat-${uniqueId}`,
      chatSessionStorageId: `${uniqueId}-chat`,
    }
  }, [browserId])
}

export const StaffAIAssistantProvider = ({ children }: Props) => {
  const overlay = useDisclosure()
  const [messages, setMessages] = useState<StaffAIChatMessage[]>([])
  const [isAssistantTyping, setIsAssistantTyping] = useState(false)
  const [activeSessionId, setActiveSessionId] = useState<number | null>(null)
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const { browserId } = usePersistentIds()
  const generateNewChatStorageIds = useNewChatStorageIds(browserId)

  // Chat storage IDs for the current session
  // Generate new IDs when there's no active session (new conversation)
  const [chatStorageIds, setChatStorageIds] = useState<ChatStorageIds>(() =>
    generateNewChatStorageIds()
  )

  // Start a new conversation - clears messages, active session, and generates new storage IDs
  const startNewConversation = useCallback(() => {
    setMessages([])
    setIsAssistantTyping(false)
    setActiveSessionId(null)
    setChatStorageIds(generateNewChatStorageIds())
    setIsLoadingSession(false)
  }, [generateNewChatStorageIds])

  const handleSetActiveSessionId = useCallback((sessionId: number | null) => {
    setActiveSessionId(sessionId)
    if (sessionId === null) {
      // When clearing the active session, also clear loading state
      setIsLoadingSession(false)
    }
  }, [])

  const value = useMemo<StaffAIAssistantContextValue>(
    () => ({
      overlay,
      messages,
      setMessages,
      isAssistantTyping,
      setIsAssistantTyping,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      setActiveSessionId: handleSetActiveSessionId,
      setIsLoadingSession,
      startNewConversation,
    }),
    [
      overlay,
      messages,
      isAssistantTyping,
      activeSessionId,
      chatStorageIds,
      isLoadingSession,
      handleSetActiveSessionId,
      startNewConversation,
    ]
  )

  return (
    <StaffAIAssistantContext.Provider value={value}>{children}</StaffAIAssistantContext.Provider>
  )
}

// Helper hook to get session preview info for display
export const useSessionPreviewTitle = (
  session: StaffAIChatSessionPreviewFragment | null
): string => {
  if (!session?.previewChat?.question) {
    return 'New Conversation'
  }
  // Truncate the question if it's too long
  const question = session.previewChat.question
  if (question.length > 50) {
    return `${question.substring(0, 47)}...`
  }
  return question
}
